/**
 * @file User / material analysis UI — pages/Dashboard/User/MaterialAnalysisInput/components/ReliabilityStandardsCard.tsx.
 */

import React, { useMemo } from "react";
import { DropdownOption } from "../../../../../interfaces/dropdownInterfaces";

interface ReliabilityStandardsCardProps {
  dataReviewOptions: DropdownOption[];
  dataCompletenessOptions: DropdownOption[];
  dataAcquisitionOptions: DropdownOption[];
  dataReview: string;
  dataCompleteness: string;
  dataAcquisition: string;
  onDataReviewChange: (value: string) => void;
  onDataCompletenessChange: (value: string) => void;
  onDataAcquisitionChange: (value: string) => void;
  disabled?: boolean;
}

const dropdownOptionValue = (option: DropdownOption): string =>
  String(option.value ?? option.id);

const resolveDropdownLabel = (option: DropdownOption): string =>
  option.label ?? dropdownOptionValue(option);

const recommendedValue = (
  options: DropdownOption[],
  scoreField: "DQI_Score" | "Completeness_Score" = "DQI_Score"
): string => {
  if (!options.length) return "";
  const [best] = [...options].sort(
    (a, b) => (b[scoreField] ?? b.DQI_Score ?? 0) - (a[scoreField] ?? a.DQI_Score ?? 0)
  );
  return dropdownOptionValue(best);
};

/** Exported const — Reliability Standards Card. */
export const ReliabilityStandardsCard: React.FC<ReliabilityStandardsCardProps> = ({
  dataReviewOptions,
  dataCompletenessOptions,
  dataAcquisitionOptions,
  dataReview,
  dataCompleteness,
  dataAcquisition,
  onDataReviewChange,
  onDataCompletenessChange,
  onDataAcquisitionChange,
  disabled = false,
}) => {
  const recommendedStandards = useMemo(
    () => ({
      dataReview: recommendedValue(dataReviewOptions),
      dataCompleteness: recommendedValue(dataCompletenessOptions, "Completeness_Score"),
      dataAcquisition: recommendedValue(dataAcquisitionOptions),
    }),
    [dataReviewOptions, dataCompletenessOptions, dataAcquisitionOptions]
  );

  // Use form values if set, otherwise use recommended values
  const currentDataReview = dataReview || recommendedStandards.dataReview;
  const currentDataCompleteness = dataCompleteness || recommendedStandards.dataCompleteness;
  const currentDataAcquisition = dataAcquisition || recommendedStandards.dataAcquisition;

  const standardsSelectOptions = useMemo(
    () => ({
      review: dataReviewOptions.map((option) => ({
        value: dropdownOptionValue(option),
        label: resolveDropdownLabel(option),
      })),
      completeness: dataCompletenessOptions.map((option) => ({
        value: dropdownOptionValue(option),
        label: resolveDropdownLabel(option),
      })),
      acquisition: dataAcquisitionOptions.map((option) => ({
        value: dropdownOptionValue(option),
        label: resolveDropdownLabel(option),
      })),
    }),
    [dataReviewOptions, dataCompletenessOptions, dataAcquisitionOptions]
  );

  return (
    <aside className="analysis-card analysis-info-card">
      <header className="analysis-card__header">
        <h3>ICoMEC Data Reliability Standards</h3>
        <p>
          Data Reliability standards refer to ICoMEC&apos;s default standards for data quality, based on:
          <br />
          – Data Review: Has the data been independently reviewed
          <br />
          – Data Completeness: How complete is it
          <br />
          – Data Acquisition: How was it collected
          <br />
          Together, these help filter &apos;reliable&apos; data for your study.
        </p>
      </header>

      <div className="three-field-row">
        <div className="three-field-cell">
          <label>
            Data Review <span className="required">*</span>
          </label>
          <select 
            value={currentDataReview} 
            onChange={(event) => onDataReviewChange(event.target.value)}
            disabled={disabled}
          >
            {standardsSelectOptions.review.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="three-field-cell">
          <label>
            Data Completeness <span className="required">*</span>
          </label>
          <select 
            value={currentDataCompleteness} 
            onChange={(event) => onDataCompletenessChange(event.target.value)}
            disabled={disabled}
          >
            {standardsSelectOptions.completeness.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="three-field-cell">
          <label>
            Data Acquisition <span className="required">*</span>
          </label>
          <select 
            value={currentDataAcquisition} 
            onChange={(event) => onDataAcquisitionChange(event.target.value)}
            disabled={disabled}
          >
            {standardsSelectOptions.acquisition.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>
    </aside>
  );
};

